/**
 * Tagada Rx — settings (merchant white-label).
 *
 * Merchants see status + mode only. Platform credentials stay behind
 * account-manager privilege and never name the clinical vendor.
 */

'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, MapPin, ShieldCheck } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { useStoreNavigate } from '@/hooks/use-app-navigate';
import { useAccountManager } from '@/hooks/use-account-manager';
import { RxPageShell } from '@/components/rx';
import {
  useRxBrandConfig,
  useRxConfigureBrandClinical,
  useRxIntegrationStatus,
  useRxSetAllowedStates,
} from '@/lib/api/rx';

const US_STATE_CODES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL',
  'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME',
  'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH',
  'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI',
  'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI',
  'WY',
];

export default function RxClinicalConfigPage() {
  const { t } = useTranslation('rx');
  const { id: storeId } = useStoreNavigate();
  const { toast } = useToast();
  const { isAccountManager } = useAccountManager();
  const integrationStatus = useRxIntegrationStatus(storeId);
  const brandConfig = useRxBrandConfig(storeId);
  const configure = useRxConfigureBrandClinical();

  const [clientId, setClientId] = React.useState('');
  const [clientSecret, setClientSecret] = React.useState('');
  const [webhookSecret, setWebhookSecret] = React.useState('');
  const [externalEnvironmentId, setExternalEnvironmentId] = React.useState('');
  const [externalPartnerId, setExternalPartnerId] = React.useState('');
  const [defaultPharmacyId, setDefaultPharmacyId] = React.useState('');

  const rxActive = integrationStatus.data?.state === 'active';
  const brandActive = brandConfig.data?.status === 'active';
  const mode =
    integrationStatus.data?.state === 'active'
      ? integrationStatus.data.settings.operatingMode
      : null;

  const handleSave = async () => {
    try {
      await configure.mutateAsync({
        storeId,
        credentials: {
          clientId: clientId || undefined,
          clientSecret: clientSecret || undefined,
          webhookSecret: webhookSecret || undefined,
          externalEnvironmentId: externalEnvironmentId || undefined,
          externalPartnerId: externalPartnerId || undefined,
        },
        defaultPharmacyId: defaultPharmacyId || undefined,
        activate: true,
      });
      toast({
        title: t('clinicalConfig.toasts.settingsSavedTitle'),
        description: t('clinicalConfig.toasts.settingsSavedDescription'),
      });
      brandConfig.refetch();
    } catch (err) {
      toast({
        title: t('clinicalConfig.toasts.saveFailedTitle'),
        description: err instanceof Error ? err.message : t('clinicalConfig.toasts.unknownError'),
        variant: 'destructive',
      });
    }
  };

  return (
    <RxPageShell
      title={t('clinicalConfig.title')}
      description={t('clinicalConfig.description')}
    >
      {!rxActive ? (
        <div className="text-muted-foreground rounded-lg border px-4 py-6 text-sm">
          {t('clinicalConfig.notActive')}
        </div>
      ) : (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <ShieldCheck className="h-4 w-4 text-primary" />
                {t('clinicalConfig.status.title')}
              </CardTitle>
              <CardDescription>
                {t('clinicalConfig.status.description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-3">
                <div>
                  <dt className="text-muted-foreground">{t('clinicalConfig.status.statusLabel')}</dt>
                  <dd className="mt-1">
                    <Badge variant={brandActive ? 'default' : 'secondary'}>
                      {brandActive
                        ? t('clinicalConfig.status.connected')
                        : t('clinicalConfig.status.pendingSetup')}
                    </Badge>
                  </dd>
                </div>
                {/* Internal routing details: shown to Tagada staff only, a brand cannot change them. */}
                {isAccountManager ? (
                  <>
                  <div>
                    <dt className="text-muted-foreground">{t('clinicalConfig.status.operatingMode')}</dt>
                    <dd className="mt-1 font-semibold">
                      {mode === 'affiliate'
                        ? t('clinicalConfig.status.modeMarketplace')
                        : mode === 'direct'
                          ? t('clinicalConfig.status.modeDirect')
                          : t('clinicalConfig.status.modeEmpty')}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">{t('clinicalConfig.status.pharmacy')}</dt>
                    <dd className="mt-1 font-semibold">
                      {brandConfig.data?.defaultPharmacyId
                        ? t('clinicalConfig.status.pharmacyCustom')
                        : t('clinicalConfig.status.pharmacyTagada')}
                    </dd>
                  </div>
                  </>
                ) : null}
              </dl>
              {!brandActive && !isAccountManager ? (
                <p className="text-muted-foreground mt-4 text-sm">
                  {t('clinicalConfig.status.pendingHint')}
                </p>
              ) : null}
            </CardContent>
          </Card>

          {/* The service area follows clinician licensing, which Tagada manages. */}
          {isAccountManager ? (
            <ServiceAreaCard
              storeId={storeId}
              allowedStates={brandConfig.data?.allowedStates ?? null}
              onSaved={() => brandConfig.refetch()}
            />
          ) : null}

          {isAccountManager ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('clinicalConfig.credentials.title')}</CardTitle>
                <CardDescription>
                  {t('clinicalConfig.credentials.description')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="rx-client-id">{t('clinicalConfig.credentials.clientIdLabel')}</Label>
                    <Input
                      id="rx-client-id"
                      value={clientId}
                      onChange={(e) => setClientId(e.target.value)}
                      placeholder={t('clinicalConfig.credentials.clientIdPlaceholder')}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rx-client-secret">{t('clinicalConfig.credentials.clientSecretLabel')}</Label>
                    <Input
                      id="rx-client-secret"
                      type="password"
                      value={clientSecret}
                      onChange={(e) => setClientSecret(e.target.value)}
                      placeholder={t('clinicalConfig.credentials.clientSecretPlaceholder')}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rx-webhook-secret">{t('clinicalConfig.credentials.webhookSecretLabel')}</Label>
                    <Input
                      id="rx-webhook-secret"
                      type="password"
                      value={webhookSecret}
                      onChange={(e) => setWebhookSecret(e.target.value)}
                      placeholder={t('clinicalConfig.credentials.webhookSecretPlaceholder')}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rx-env">{t('clinicalConfig.credentials.environmentIdLabel')}</Label>
                    <Input
                      id="rx-env"
                      value={externalEnvironmentId}
                      onChange={(e) => setExternalEnvironmentId(e.target.value)}
                      placeholder={t('clinicalConfig.credentials.environmentIdPlaceholder')}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rx-partner">{t('clinicalConfig.credentials.partnerIdLabel')}</Label>
                    <Input
                      id="rx-partner"
                      value={externalPartnerId}
                      onChange={(e) => setExternalPartnerId(e.target.value)}
                      placeholder={t('clinicalConfig.credentials.partnerIdPlaceholder')}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rx-pharmacy">{t('clinicalConfig.credentials.pharmacyLabel')}</Label>
                    <Input
                      id="rx-pharmacy"
                      value={defaultPharmacyId}
                      onChange={(e) => setDefaultPharmacyId(e.target.value)}
                      placeholder={t('clinicalConfig.credentials.pharmacyPlaceholder')}
                    />
                  </div>
                </div>
                <Button onClick={handleSave} disabled={configure.isPending}>
                  {configure.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {t('clinicalConfig.credentials.saveActivate')}
                </Button>
              </CardContent>
            </Card>
          ) : null}
        </div>
      )}
    </RxPageShell>
  );
}

/**
 * US-state licensing gate. Empty selection = ship everywhere. When
 * restricted, checkout blocks unsupported states before payment and
 * case submission enforces it server-side.
 */
function ServiceAreaCard({
  storeId,
  allowedStates,
  onSaved,
}: {
  storeId: string;
  allowedStates: string[] | null;
  onSaved: () => void;
}) {
  const { t } = useTranslation('rx');
  const { toast } = useToast();
  const setAllowedStates = useRxSetAllowedStates();
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [restricted, setRestricted] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);

  React.useEffect(() => {
    if (dirty) return;
    setRestricted(Boolean(allowedStates && allowedStates.length > 0));
    setSelected(new Set(allowedStates ?? []));
  }, [allowedStates, dirty]);

  const toggleState = (code: string) => {
    setDirty(true);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const handleSave = async () => {
    try {
      await setAllowedStates.mutateAsync({
        storeId,
        allowedStates: restricted ? [...selected] : null,
      });
      setDirty(false);
      toast({
        title: t('clinicalConfig.toasts.serviceAreaSavedTitle'),
        description: restricted
          ? t('clinicalConfig.toasts.serviceAreaSavedRestricted', {
              count: selected.size,
              plural: selected.size === 1 ? '' : 's',
            })
          : t('clinicalConfig.toasts.serviceAreaSavedAll'),
      });
      onSaved();
    } catch (err) {
      toast({
        title: t('clinicalConfig.toasts.saveFailedTitle'),
        description: err instanceof Error ? err.message : t('clinicalConfig.toasts.unknownError'),
        variant: 'destructive',
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <MapPin className="h-4 w-4 text-primary" />
          {t('clinicalConfig.serviceArea.title')}
        </CardTitle>
        <CardDescription>
          {t('clinicalConfig.serviceArea.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant={restricted ? 'outline' : 'default'}
            onClick={() => {
              setDirty(true);
              setRestricted(false);
            }}
          >
            {t('clinicalConfig.serviceArea.allStates')}
          </Button>
          <Button
            type="button"
            size="sm"
            variant={restricted ? 'default' : 'outline'}
            onClick={() => {
              setDirty(true);
              setRestricted(true);
            }}
          >
            {t('clinicalConfig.serviceArea.selectedStatesOnly')}
          </Button>
          {restricted ? (
            <span className="text-muted-foreground text-xs">
              {t('clinicalConfig.serviceArea.selectionCount', {
                selected: selected.size,
                total: US_STATE_CODES.length,
              })}
            </span>
          ) : null}
        </div>

        {restricted ? (
          <div className="grid grid-cols-6 gap-1.5 sm:grid-cols-9 md:grid-cols-12">
            {US_STATE_CODES.map((code) => {
              const isOn = selected.has(code);
              return (
                <button
                  key={code}
                  type="button"
                  aria-pressed={isOn}
                  onClick={() => toggleState(code)}
                  className={`rounded-md border px-1.5 py-1 text-xs font-medium transition-colors ${
                    isOn
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-input bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground'
                  }`}
                >
                  {code}
                </button>
              );
            })}
          </div>
        ) : null}

        <Button
          onClick={handleSave}
          disabled={setAllowedStates.isPending || (restricted && selected.size === 0)}
        >
          {setAllowedStates.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {t('clinicalConfig.serviceArea.save')}
        </Button>
        {restricted && selected.size === 0 ? (
          <p className="text-destructive text-xs">{t('clinicalConfig.serviceArea.needOneState')}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}
